import { useEffect, useRef } from 'react';
import { CategoryListing } from '@/components/CategoryListing';
import { JsonScriptId } from '@/constants';
import { useCategoryListing } from '@/hooks/useCategoryListing';
import type { CategoryData } from '@/types';
import { getEmbeddedJson } from '@/utils/getEmbeddedJson';
import { setQueryId, getAuthUserToken } from '@/utils/searchAnalytics';
import { useAuthLinks } from '@/hooks/useAuthLinks';
import { useBreadcrumb } from '@/hooks/useBreadcrumb';
import { useComponentFactory } from '@/hooks/useComponentFactory';
import { useMenuToggle } from '@/hooks/useMenuToggle';
import { useMiniBasket } from '@/hooks/useMiniBasket';
import { useNewsletterSignup } from '@/hooks/useNewsletterSignup';
import { useProductPurchasing } from '@/hooks/useProductPurchasing';

export const CategoryApp = () => {
  const initial = getEmbeddedJson(JsonScriptId.Category) as CategoryData;
  const listing = useCategoryListing(initial);
  const authLinks = useAuthLinks();
  const menuToggle = useMenuToggle();
  const miniBasket = useMiniBasket();
  const breadcrumb = useBreadcrumb();
  const componentFactory = useComponentFactory();
  const purchaseOptions = useProductPurchasing();
  const newsletterSignup = useNewsletterSignup();

  // Algolia insights: set queryId cookie on every query change
  useEffect(() => {
    if (listing.algoliaQueryId) {
      setQueryId(listing.algoliaQueryId);
    }
  }, [listing.algoliaQueryId]);

  // Fire "Category Page Viewed" once on mount with the server-built filters
  // (includes categories + any preselected facets with correct Algolia attribute names)
  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    window.dataLayer?.push({
      event: 'Category Page Viewed',
      authUserToken: getAuthUserToken(),
      filters: initial.algoliaFilters ?? [],
    });
  }, [initial.algoliaFilters]);
  
  return (
    <>
      <CategoryListing {...listing} />
      {authLinks}
      {menuToggle}
      {miniBasket}
      {breadcrumb}
      {purchaseOptions}
      {componentFactory}
      {newsletterSignup}
    </>
  );
};
